/**
 * Keeps the browser's permission document current without waiting for the
 * services' permission cache (INTRO-17).
 *
 * INTRO-13 made a purpose change visible on the next request, but only for the
 * person who made it from their own panel. When an admin approves or ends
 * someone else's purpose, nothing told that person's browser, so it kept
 * sending an old `X-Perm-Version` and the change waited out the services'
 * cache (up to 30 s). Two cheap mechanisms close that:
 *
 *  1. Version poll. Every few seconds, while the tab is visible, read
 *     `GET /auth/permissions/me/version` (a bare integer, bearer token in the
 *     Authorization header like any other call). When it is newer than the
 *     version of the document we hold, read the document again. Coming back to
 *     the tab checks at once.
 *  2. Expiry timer. Nothing bumps the version when a purpose runs out, so the
 *     document itself says when: a timer fires at the earliest `expires_at` of
 *     its active purposes and reads the document again, whose purposes list
 *     the server has already trimmed.
 *
 * Why not a pushed channel: a socket or stream into auth-service would be a new
 * authenticated, long-lived surface (single-use tickets, origin checks, session
 * caps, replica fan-out) to save a few seconds over a poll that reuses the
 * normal bearer-token request. Failures here are silent and harmless: the
 * services' own cache still bounds staleness at 30 s, as before.
 */

export interface PermSyncDeps {
  /** `GET /auth/permissions/me/version`. Rejects on any failure. */
  fetchVersion: () => Promise<number>;
  /** Version of the document we hold, or undefined when none is loaded. */
  getVersion: () => number | undefined;
  /** Reads the document again; resolves false when it could not. */
  refresh: () => Promise<boolean>;
  /** `expires_at` values (ISO) of the active purposes in the document we hold. */
  getExpiries: () => string[];
  /** Calls `listener` whenever the document we hold changes; returns unsubscribe. */
  subscribe: (listener: () => void) => () => void;
  isVisible?: () => boolean;
  now?: () => number;
  intervalMs?: number;
}

export const PERM_POLL_MS = 5_000;
const MAX_BACKOFF_MS = 60_000;
const EXPIRY_GRACE_MS = 500;
const MAX_TIMEOUT_MS = 2_000_000_000; // setTimeout caps near 2^31 ms
const RETRY_FLOOR_MS = 2_000;

/** Earliest future-or-past expiry in epoch ms, or null (none, or all unreadable). */
export function nextExpiry(expiries: string[]): number | null {
  let best: number | null = null;
  for (const raw of expiries) {
    // The server always sends an offset; a bare timestamp would read as local.
    const text = String(raw);
    const ms = Date.parse(/(Z|[+-]\d\d:?\d\d)$/.test(text) ? text : `${text}Z`);
    if (Number.isNaN(ms)) continue;
    if (best === null || ms < best) best = ms;
  }
  return best;
}

export function startPermSync(deps: PermSyncDeps): () => void {
  const interval = deps.intervalMs ?? PERM_POLL_MS;
  const visible = deps.isVisible ?? (() => typeof document === 'undefined' || !document.hidden);
  const now = deps.now ?? Date.now;

  let stopped = false;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;
  let checking = false;
  // Expiry we already re-read for, and how many times: a clock that runs ahead
  // of the server's must not turn into a tight loop.
  let expiryKey: number | null = null;
  let expiryTries = 0;

  const schedulePoll = () => {
    if (stopped) return;
    clearTimeout(pollTimer);
    const wait = failures ? Math.min(interval * 2 ** failures, MAX_BACKOFF_MS) : interval;
    pollTimer = setTimeout(() => { void check(); }, wait);
  };

  const check = async () => {
    if (stopped || checking) return;
    if (!visible()) { schedulePoll(); return; }
    checking = true;
    try {
      const have = deps.getVersion();
      if (have === undefined) return; // no document yet: the first load is not ours
      const latest = await deps.fetchVersion();
      failures = 0;
      if (latest > have) await deps.refresh();
    } catch {
      failures = Math.min(failures + 1, 6);
    } finally {
      checking = false;
      schedulePoll();
    }
  };

  const armExpiry = () => {
    if (stopped) return;
    clearTimeout(expiryTimer);
    const at = nextExpiry(deps.getExpiries());
    if (at === null) { expiryKey = null; expiryTries = 0; return; }
    if (at !== expiryKey) { expiryKey = at; expiryTries = 0; }
    const base = at - now() + EXPIRY_GRACE_MS;
    // A retry for the same expiry waits longer each time (2, 4, 8 ... 60 s).
    const retry = expiryTries ? Math.min(RETRY_FLOOR_MS * 2 ** (expiryTries - 1), MAX_BACKOFF_MS) : 0;
    const delay = Math.min(Math.max(base, retry, 0), MAX_TIMEOUT_MS);
    expiryTimer = setTimeout(async () => {
      if (stopped) return;
      if (now() + EXPIRY_GRACE_MS >= at) {
        expiryTries += 1;
        await deps.refresh();
      }
      armExpiry();
    }, delay);
  };

  const onVisible = () => { if (visible()) void check(); };
  const unsubscribe = deps.subscribe(armExpiry);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  if (typeof window !== 'undefined') window.addEventListener('focus', onVisible);
  armExpiry();
  schedulePoll();

  return () => {
    stopped = true;
    clearTimeout(pollTimer);
    clearTimeout(expiryTimer);
    unsubscribe();
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    if (typeof window !== 'undefined') window.removeEventListener('focus', onVisible);
  };
}
